import { isEmpty, isNil } from 'lodash';

export const NumberFormat = {
  CURRENCY: 'CURRENCY',
  FLOAT: 'FLOAT',
  PERCENT: 'PERCENT',
} as const;

export type NumberFormat = (typeof NumberFormat)[keyof typeof NumberFormat];

// Formatting constants
const CURRENCY_PRECISION = 2;
const FLOAT_PRECISION = 3;

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: CURRENCY_PRECISION,
  maximumFractionDigits: CURRENCY_PRECISION,
});

const percentFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: FLOAT_PRECISION,
});

const floatFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: FLOAT_PRECISION,
});

const editableWholeCurrencyFormatter = new Intl.NumberFormat('en-US', {
  useGrouping: false,
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const editableFractionalCurrencyFormatter = new Intl.NumberFormat('en-US', {
  useGrouping: false,
  minimumFractionDigits: CURRENCY_PRECISION,
  maximumFractionDigits: CURRENCY_PRECISION,
});

const editableFloatFormatter = new Intl.NumberFormat('en-US', {
  useGrouping: false,
  maximumFractionDigits: FLOAT_PRECISION,
});

const formatters = {
  currency: (value: number) => currencyFormatter.format(value),
  percent: (value: number) => percentFormatter.format(value),
  float: (value: number) => floatFormatter.format(value),
  editableWholeCurrency: (value: number) => editableWholeCurrencyFormatter.format(value),
  editableFractionalCurrency: (value: number) => editableFractionalCurrencyFormatter.format(value),
  editableFloat: (value: number) => editableFloatFormatter.format(value),
};

/*
Number formatting rules.

There are 3 formats: Basic, Currency, Percent. And 3 states: input, saved value, display.

- Basic: no difference between input, saved value, and display. All can be float. 1.23 = 1.23, 123 = 123, 123.00 = 123.00
- Currency:
  - Input can be float. If no decimal is provided, .00 is assumed.
  - Saved value is always an integer (cents). 1.23 = 123, 123 = 12300, 123.00 = 12300
  - Display is formatted currency. This looks like input, but always with a decimal and prepended with a dollar sign.
- Percent:
  - Input can be float.
  - Saved value is input / 100;
  - Display is a float converting decimal to percent, saved value * 100 followed by a %. 1.23 = 123%, 123 = 12300%, 123.00 = 12300%
*/

export function toEditableValue(value: number | null | undefined, format?: NumberFormat): string {
  if (isNil(value) || isNaN(value)) {
    return '';
  }

  switch (format) {
    case NumberFormat.CURRENCY: {
      const isWholeNumber = Math.round(value / 100) === value / 100;
      const formatter = isWholeNumber
        ? formatters.editableWholeCurrency
        : formatters.editableFractionalCurrency;
      return formatter(value / 100);
    }
    case NumberFormat.PERCENT:
      return formatters.editableFloat(value * 100);
    default:
      return formatters.editableFloat(value);
  }
}

export function toFormattedValue(value: number | null | undefined, format?: NumberFormat): string {
  if (isNil(value) || isNaN(value)) {
    return '';
  }

  switch (format) {
    case NumberFormat.CURRENCY:
      return formatters.currency(value / 100);
    case NumberFormat.PERCENT:
      return formatters.percent(value);
    default:
      return formatters.float(value);
  }
}

export function toStoredValue(editableValue: string, format?: NumberFormat): number | null {
  try {
    // remove all non-numeric input
    const numericString = removeNonNumericInput(editableValue);
    if (isEmpty(numericString)) {
      return null;
    }

    let numericValue = Number(numericString);
    switch (format) {
      case NumberFormat.CURRENCY:
        numericValue = Math.round(numericValue * 100);
        break;
      case NumberFormat.PERCENT:
        numericValue = Number(formatters.editableFloat(numericValue)) / 100;
        break;
      default: {
        numericValue = Number(formatters.editableFloat(numericValue));
      }
    }

    // ensure that the above format handling still returns a number
    if (isNaN(numericValue)) {
      return null;
    }
    return numericValue;
  } catch (error) {
    console.warn('Error parsing number', error);
    return null;
  }
}

export function removeInvalidInput(input: string): string {
  // remove all non-numeric input, allowing number-related characters
  return input.replace(/[^\d.,$% -]/g, '');
}

function removeNonNumericInput(input: string): string {
  // remove all non-numeric input
  return input.replace(/[^\d.-]/g, '');
}
